import { Injectable, Injector, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../services/alert.service';

/**
 * Surfaces sub-user permission 403s (customer-users guide §4.3) as a soft
 * "you don't have access" toast:
 *
 * - `MODULE_<REF>_FORBIDDEN` — the sub-user's type lacks that module
 * - `MAIN_ACCOUNT_ONLY` — a main-account-only surface
 *
 * These are NOT session-ending — no logout, and the error is re-thrown so
 * callers keep their own handling. A short suppression window avoids a
 * toast storm when one screen fires several gated requests at once.
 *
 * AlertService/TranslateService are resolved LAZILY at error time, never in
 * the constructor: interceptors are built while HttpClient's handler chain
 * is under construction, and TranslateService's loader (TranslateHttpLoader)
 * itself needs HttpClient — eager injection here is an NG0200 cycle that
 * kills bootstrap.
 */
@Injectable()
export class PermissionErrorInterceptor implements HttpInterceptor {
  private readonly injector = inject(Injector);

  private lastNoticeAt = 0;
  private static readonly NOTICE_WINDOW_MS = 3000;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 403) {
          const code = typeof (err.error as any)?.error === 'string' ? (err.error as any).error : '';
          const isModule = /^MODULE_[A-Z_]+_FORBIDDEN$/.test(code);
          if (isModule || code === 'MAIN_ACCOUNT_ONLY') {
            const now = Date.now();
            if (now - this.lastNoticeAt > PermissionErrorInterceptor.NOTICE_WINDOW_MS) {
              this.lastNoticeAt = now;
              const alert = this.injector.get(AlertService);
              const translate = this.injector.get(TranslateService);
              alert.showWarning(translate.instant(
                isModule ? 'CUSTOMER_USERS.NO_MODULE_ACCESS' : 'CUSTOMER_USERS.OWNER_ONLY'
              ));
            }
          }
        }
        return throwError(() => err);
      }),
    );
  }
}
